// Uses BreakdownFormField for all 12 financial fields — each field shows 3 named
// input slots (Dentrix / Tracker 1 / Tracker 2) with an auto-calculated total.
import BreakdownFormField from "./BreakdownFormField";
import { Badge } from "@common";
import { normalizeTypeForUi, pickBadgeColor } from "@utils/utils";
import { REPORT_COLUMN_CONFIGS } from "@utils";

// Derived from shared REPORT_COLUMN_CONFIGS — maps backend key → local form field name
const BACKEND_TO_FORM_FIELD = Object.fromEntries(
  REPORT_COLUMN_CONFIGS.map(({ key, formField }) => [key, formField]),
);

export default function CostProductionBreakdownForm({
  values = {},
  breakdowns = {},
  onBreakdownChange,
  errors = {},
  showErrors = false,
  disabled = false,
  title = "Cost & Production Breakdown",
  practitioner,
  hiddenColumns = [],
  includedAdjustmentDeduction = false,
  onIncludedAdjustmentDeductionChange,
  singleInput = false,
  fieldComments = {},
  pdfSuggestedLabCost = "",
  pdfSuggestedLabCostComment = "",
  pdfSuggestedLabCostWarning = "",
  onPdfSuggestedLabCostWarningClick,
  // "Suggested: $X (N invoices)" from the day's Invisalign invoice PDFs.
  invisalignSuggestionText = "",
  extraFieldsBeforeFees = null,
}) {
  const isFieldHidden = (backendKey) =>
    hiddenColumns.includes(backendKey) || hiddenColumns.includes(BACKEND_TO_FORM_FIELD[backendKey]);

  const handleBreakdown = (field) => (entries) => {
    if (onBreakdownChange) onBreakdownChange(field, entries);
  };

  const practitionerName = practitioner?.name || practitioner?.practitioner || "";
  const practitionerType = practitioner?.practitioner_type
    ? String(practitioner.practitioner_type)
        .toUpperCase()
        .replace(/[[\]"']/g, "")
        .trim()
    : "";
  const practitionerTypeDisplay = practitioner?.practitioner_type_display || practitionerType;
  const typeForUi = normalizeTypeForUi(practitioner?.practitioner_type) || practitionerType;
  const isHygienist = typeForUi === "HYG";

  const paidBasedOn = String(practitioner?.paid_based_on || "")
    .trim()
    .toLowerCase();
  const adjAttachField = paidBasedOn === "production" ? "production" : "collection";
  const adjAdjLabel =
    adjAttachField === "production"
      ? "Production already has Production Adjustment deducted"
      : "Collection already has Collection Adjustment deducted";
  const showAdjToggle = !isHygienist && typeof onIncludedAdjustmentDeductionChange === "function";

  const readOnlyInputClass =
    "mt-2 w-full rounded-xl border border-palette-slate-ring bg-palette-slate-muted px-3 py-2 text-sm text-palette-slate-text";

  const formatReadOnlyAmount = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const number = Number(value);
    if (Number.isNaN(number)) return String(value);
    return number.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const renderAdjToggle = () => {
    if (!showAdjToggle) return null;
    return (
      <label className="mt-1 flex items-center gap-2 text-xs text-palette-slate-badge select-none">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 rounded border-palette-slate-ring text-palette-indigo-badge focus:ring-palette-indigo-label"
          checked={!!includedAdjustmentDeduction}
          disabled={disabled}
          onChange={(e) => onIncludedAdjustmentDeductionChange(e.target.checked)}
        />
        <span>{adjAdjLabel}</span>
      </label>
    );
  };

  return (
    <>
      <div className="mb-4">
        <div className="text-sm font-semibold text-palette-slate-badge">{title}</div>

        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-bold text-palette-slate-primary leading-tight">
            {practitionerName || "—"}
          </div>

          {(typeForUi || practitionerType) && (
            // pickBadgeColor replaces a hardcoded `typeForUi === "DDS" ? "neutral" : "success"` — now handles all 16 types
            <Badge color={pickBadgeColor(typeForUi)}>{practitionerTypeDisplay}</Badge>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <BreakdownFormField
            label="Production"
            entries={breakdowns.production || []}
            onChange={handleBreakdown("production")}
            total={values.production || ""}
            disabled={disabled}
            error={errors.production}
            showError={showErrors}
            required={true}
            singleInput={singleInput}
          />
          {adjAttachField === "production" && renderAdjToggle()}
        </div>

        <div>
          <BreakdownFormField
            label="Collection"
            entries={breakdowns.collection || []}
            onChange={handleBreakdown("collection")}
            total={values.collection || ""}
            disabled={disabled}
            error={errors.collection}
            showError={showErrors}
            required={true}
            singleInput={singleInput}
          />
          {adjAttachField === "collection" && renderAdjToggle()}
        </div>

        {!isHygienist && (
          <>
            {!isFieldHidden("lab_costs") && (
              <>
                <div className="mb-3">
                  <label className="block text-sm font-medium">PDF Suggested Lab Costs</label>
                  <input
                    type="text"
                    value={formatReadOnlyAmount(pdfSuggestedLabCost)}
                    disabled
                    readOnly
                    placeholder="0.00"
                    className={readOnlyInputClass}
                  />
                  {pdfSuggestedLabCostComment && (
                    <p className="mt-1 text-xs text-palette-slate-secondary">
                      {pdfSuggestedLabCostComment}
                    </p>
                  )}
                  {pdfSuggestedLabCostWarning &&
                    (onPdfSuggestedLabCostWarningClick ? (
                      <button
                        type="button"
                        className="mt-1 block text-left text-xs font-medium text-palette-rose-badge underline decoration-palette-rose-ring underline-offset-2 hover:text-palette-rose-heading"
                        onClick={onPdfSuggestedLabCostWarningClick}
                      >
                        {pdfSuggestedLabCostWarning}
                      </button>
                    ) : (
                      <p className="mt-1 text-xs font-medium text-palette-rose-badge">
                        {pdfSuggestedLabCostWarning}
                      </p>
                    ))}
                </div>
                <BreakdownFormField
                  label="Lab Costs"
                  entries={breakdowns.lab || []}
                  onChange={handleBreakdown("lab")}
                  total={values.lab || ""}
                  disabled={disabled}
                  error={errors.lab}
                  showError={showErrors}
                  singleInput={singleInput}
                  helperText={fieldComments.lab}
                />
              </>
            )}

            {!isFieldHidden("material_costs") && (
              <BreakdownFormField
                label="Material Costs"
                entries={breakdowns.material || []}
                onChange={handleBreakdown("material")}
                total={values.material || ""}
                disabled={disabled}
                error={errors.material}
                showError={showErrors}
                singleInput={singleInput}
              />
            )}

            {!isFieldHidden("invisalign_costs") && (
              <BreakdownFormField
                label="Invisalign Costs"
                entries={breakdowns.invisalign || []}
                onChange={handleBreakdown("invisalign")}
                total={values.invisalign || ""}
                disabled={disabled}
                error={errors.invisalign}
                showError={showErrors}
                singleInput={singleInput}
                helperText={invisalignSuggestionText}
              />
            )}

            {!isFieldHidden("anesthesia_fee") && (
              <BreakdownFormField
                label="Anesthesia Fee"
                entries={breakdowns.anesthesia_fee || []}
                onChange={handleBreakdown("anesthesia_fee")}
                total={values.anesthesia_fee || ""}
                disabled={disabled}
                error={errors.anesthesia_fee}
                showError={showErrors}
                singleInput={singleInput}
              />
            )}

            {extraFieldsBeforeFees}

            {!isFieldHidden("anesthesiologist_fee") && (
              <BreakdownFormField
                label="Anesthesiologist Fee"
                entries={breakdowns.anesthesiologist_fee || []}
                onChange={handleBreakdown("anesthesiologist_fee")}
                total={values.anesthesiologist_fee || ""}
                disabled={disabled}
                error={errors.anesthesiologist_fee}
                showError={showErrors}
                singleInput={singleInput}
              />
            )}

            {!isFieldHidden("facility_fee") && (
              <BreakdownFormField
                label="Facility Fee"
                entries={breakdowns.facility_fee || []}
                onChange={handleBreakdown("facility_fee")}
                total={values.facility_fee || ""}
                disabled={disabled}
                error={errors.facility_fee}
                showError={showErrors}
                singleInput={singleInput}
              />
            )}

            {!isFieldHidden("botox") && (
              <BreakdownFormField
                label="Botox"
                entries={breakdowns.botox || []}
                onChange={handleBreakdown("botox")}
                total={values.botox || ""}
                disabled={disabled}
                error={errors.botox}
                showError={showErrors}
                singleInput={singleInput}
              />
            )}

            {!isFieldHidden("production_adjustment") && (
              <BreakdownFormField
                label="Production Adjustment"
                entries={breakdowns.production_adjustment || []}
                onChange={handleBreakdown("production_adjustment")}
                total={values.production_adjustment || ""}
                disabled={disabled}
                error={errors.production_adjustment}
                showError={showErrors}
                singleInput={singleInput}
              />
            )}

            {!isFieldHidden("collection_adjustment") && (
              <BreakdownFormField
                label="Collection Adjustment"
                entries={breakdowns.collection_adjustment || []}
                onChange={handleBreakdown("collection_adjustment")}
                total={values.collection_adjustment || ""}
                disabled={disabled}
                error={errors.collection_adjustment}
                showError={showErrors}
                singleInput={singleInput}
              />
            )}
          </>
        )}

        <BreakdownFormField
          label="Hours Worked"
          entries={breakdowns.hours || []}
          onChange={handleBreakdown("hours")}
          total={values.hours || ""}
          disabled={disabled}
          error={errors.hours}
          showError={showErrors}
          required={true}
          singleInput={singleInput}
        />
      </div>
    </>
  );
}
